import React, { useEffect, useState, useRef, lazy, Suspense, useContext } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { ArrowLeft, Calendar, User, Share2, Clock } from 'lucide-react';
import ShareButtons from '../components/ShareButtons';
import AdSenseAd from '../components/AdSenseAd';
import { AuthContext } from '../App';
import { ARTICLE_ENHANCERS } from '../articleEnhancers';

// Dynamically import custom components
const CoachesCallWidget = lazy(() => import('../components/CoachesCallWidget'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SITE_URL = process.env.REACT_APP_SITE_URL || 'https://shelovesthee.com';
const API = `${BACKEND_URL}/api`;

// Component registry for dynamic rendering
const COMPONENT_REGISTRY = {
  CoachesCallWidget: CoachesCallWidget
};

// NOTE: SLTGameBridge is now initialized globally in App.js AuthProvider
// This ensures the bridge persists across page navigations and redirects

// HTML Block Renderer that executes embedded scripts
// Inline scripts already run once in this page session are re-run inside a block scope,
// so top-level const/let don't throw "already declared" when readers navigate back to an article.
const executedScripts = new Set();

const HtmlBlockRenderer = ({ html, articleId }) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  
  // Wait for bridge to be available before rendering HTML
  useEffect(() => {
    const checkBridge = () => {
      if (window.SLTGameBridge || window.SheLovesTheePolls) {
        setIsReady(true);
        return true;
      }
      return false;
    };
    
    // Check immediately
    if (checkBridge()) return;
    
    // Listen for bridge ready event
    const handleReady = () => {
      setIsReady(true);
    };
    
    window.addEventListener('slt-game-bridge-ready', handleReady);
    window.addEventListener('shelovesthee-polls-ready', handleReady);
    
    // Also check after short delays
    const t1 = setTimeout(checkBridge, 50);
    const t2 = setTimeout(checkBridge, 150);
    const t3 = setTimeout(() => setIsReady(true), 300); // Proceed anyway after 300ms
    
    return () => {
      window.removeEventListener('slt-game-bridge-ready', handleReady);
      window.removeEventListener('shelovesthee-polls-ready', handleReady);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, []);
  
  useEffect(() => {
    if (!containerRef.current || !html || !isReady) return;
    
    console.log('[HtmlBlockRenderer] Bridge ready, setting HTML content');
    
    // Set the HTML content
    containerRef.current.innerHTML = html;
    
    // Find and execute all script tags
    const scripts = containerRef.current.querySelectorAll('script');
    scripts.forEach(oldScript => {
      const newScript = document.createElement('script');
      
      // Copy attributes
      Array.from(oldScript.attributes).forEach(attr => {
        newScript.setAttribute(attr.name, attr.value);
      });
      
      // Copy inline script content
      if (oldScript.textContent) {
        const code = oldScript.textContent;
        const isClassic = !oldScript.type || oldScript.type === 'text/javascript';
        newScript.textContent = isClassic && executedScripts.has(code) ? `{\n${code}\n}` : code;
        if (isClassic) executedScripts.add(code);
      }
      
      // Replace old script with new one to execute it
      oldScript.parentNode?.replaceChild(newScript, oldScript);
    });

    // Article-specific community integrations (polls, predictions)
    const enhancer = articleId && ARTICLE_ENHANCERS[articleId];
    let cleanupEnhancer;
    try {
      cleanupEnhancer = enhancer ? enhancer(containerRef.current) : undefined;
    } catch (e) {
      console.error('[HtmlBlockRenderer] Article enhancer failed', e);
    }
    const container = containerRef.current;
    const ownedIds = Array.from(container.querySelectorAll('[id]')).map(n => n.id);

    // Scroll to hash target (e.g. #thee-comments after login return)
    const hashId = decodeURIComponent(window.location.hash.slice(1));
    if (hashId) {
      setTimeout(() => document.getElementById(hashId)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    }
    
    // Also initialize Twitter/X embeds if present
    if (html.includes('twitter.com') || html.includes('x.com')) {
      if (window.twttr && window.twttr.widgets) {
        window.twttr.widgets.load(containerRef.current);
      } else {
        // Load Twitter widgets script if not present
        const twitterScript = document.createElement('script');
        twitterScript.src = 'https://platform.twitter.com/widgets.js';
        twitterScript.async = true;
        twitterScript.charset = 'utf-8';
        document.body.appendChild(twitterScript);
      }
    }
    
    // Dispatch bridge ready events AFTER scripts are loaded
    // This triggers article scripts that registered listeners
    setTimeout(() => {
      if (window.SLTGameBridge) {
        console.log('[HtmlBlockRenderer] Dispatching slt-game-bridge-ready');
        window.dispatchEvent(new Event('slt-game-bridge-ready'));
      }
      if (window.SheLovesTheePolls) {
        console.log('[HtmlBlockRenderer] Dispatching shelovesthee-polls-ready');
        window.dispatchEvent(new Event('shelovesthee-polls-ready'));
      }
    }, 50);
    
    setTimeout(() => {
      if (window.SLTGameBridge) {
        window.dispatchEvent(new Event('slt-game-bridge-ready'));
      }
      if (window.SheLovesTheePolls) {
        window.dispatchEvent(new Event('shelovesthee-polls-ready'));
      }
    }, 500);
    
    setTimeout(() => {
      if (window.SLTGameBridge) {
        window.dispatchEvent(new Event('slt-game-bridge-ready'));
      }
      if (window.SheLovesTheePolls) {
        window.dispatchEvent(new Event('shelovesthee-polls-ready'));
      }
    }, 1500);
    
    return () => {
      cleanupEnhancer?.();
      // Remove elements the article moved outside its container (e.g. dialogs appended to <body>)
      ownedIds.forEach(id => {
        const node = document.getElementById(id);
        if (node && !container.contains(node)) node.remove();
      });
    };
  }, [html, isReady, articleId]);
  
  return (
    <div 
      ref={containerRef}
      className="my-6 html-block"
    />
  );
};

const InteractiveArticleViewer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const loadedArticleId = article?.article_id;

  // NOTE: SLTGameBridge is now initialized globally in App.js AuthProvider
  // No need to call useSLTGameBridge here anymore

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/interactive-articles/${id}`);
        const data = response.data;
        
        // If article has a redirect_url, navigate there instead
        if (data.redirect_url) {
          navigate(data.redirect_url, { replace: true });
          return;
        }
        
        setArticle(data);
      } catch (err) {
        console.error('Error fetching interactive article:', err);
        setError('Article not found');
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [id, navigate]);

  useEffect(() => {
    if (!loadedArticleId || window.location.hash) return undefined;
    const resetToTop = () => window.scrollTo(0, 0);
    resetToTop();
    const frame = window.requestAnimationFrame(resetToTop);
    const settleTimer = window.setTimeout(resetToTop, 500);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
    };
  }, [loadedArticleId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00204E] mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <h1 className="text-4xl font-bold text-[#00204E] mb-4">404</h1>
          <h2 className="text-xl font-semibold text-slate-700 mb-2">Article Not Found</h2>
          <p className="text-slate-600 mb-6">
            The article you're looking for doesn't exist or has been moved.
          </p>
          <div className="space-y-3">
            <Link 
              to="/news" 
              className="block w-full bg-[#00204E] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#003366] transition-colors"
            >
              Browse News
            </Link>
            <Link 
              to="/" 
              className="block w-full bg-slate-200 text-slate-700 py-3 px-6 rounded-lg font-semibold hover:bg-slate-300 transition-colors"
            >
              Go Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const pageUrl = `${SITE_URL}/interactive/${article.article_id}`;
  const publishDate = article.published_at ? new Date(article.published_at).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  }) : '';

  return (
    <>
      <Helmet>
        <title>{article.title ? `${article.title} | SheLovesThee` : 'SheLovesThee'}</title>
        <meta name="description" content={article.subtitle || article.title || 'HBCU Sports Coverage'} />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={article.title || 'SheLovesThee Article'} />
        <meta property="og:description" content={article.subtitle || article.title || 'HBCU Sports Coverage'} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={article.featured_image || ''} />
        <meta property="og:type" content="article" />
        <meta name="twitter:title" content={article.title || 'SheLovesThee Article'} />
        <meta name="twitter:description" content={article.subtitle || article.title || 'HBCU Sports Coverage'} />
        <meta name="twitter:image" content={article.featured_image || ''} />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Back navigation */}
        <div className="bg-[#00204E] text-white py-3">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            <Link to="/news" className="inline-flex items-center gap-2 text-sm hover:text-[#ff9900] transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to News
            </Link>
          </div>
        </div>

        <article className="max-w-[1440px] mx-auto px-4 md:px-6 py-8">
          {/* Featured Image */}
          {article.featured_image && (
            <div className="aspect-[16/9] rounded-xl overflow-hidden mb-6 shadow-lg">
              <img 
                src={article.featured_image} 
                alt={article.title}
                className="w-full h-full object-cover"
              />
            </div>
          )}

          {/* Category */}
          {article.category && (
            <span className="inline-block text-xs font-bold text-red-600 uppercase tracking-wide mb-3">
              {article.category}
            </span>
          )}

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-[#00204E] leading-tight mb-4">
            {article.title}
          </h1>

          {/* Subtitle */}
          {article.subtitle && (
            <p className="text-xl text-slate-600 mb-6">{article.subtitle}</p>
          )}

          {/* Meta info */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-6 pb-6 border-b border-slate-200">
            {article.author && (
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>{article.author}</span>
              </div>
            )}
            {publishDate && (
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>{publishDate}</span>
              </div>
            )}
          </div>

          {/* Share */}
          <div className="mb-8">
            <ShareButtons 
              title={article.title}
              url={pageUrl}
              variant="inline"
            />
          </div>

          {/* Content Blocks */}
          <div className="prose prose-lg max-w-none">
            {article.blocks?.map((block, index) => (
              <ArticleBlock key={`${article.article_id}-${index}`} block={block} articleId={article.article_id} />
            ))}
          </div>

          {/* Ad */}
          <div className="my-8">
            <AdSenseAd 
              adSlot="2800195719"
              adFormat="horizontal"
              style={{ minHeight: '90px' }}
              label="Advertisement"
            />
          </div>
        </article>
      </div>
    </>
  );
};

// Render different block types
const ArticleBlock = ({ block, articleId }) => {
  if (!block) return null;

  switch (block.type) {
    case 'text':
      return (
        <div 
          className="mb-6"
          dangerouslySetInnerHTML={{ __html: block.content || '' }}
        />
      );
    
    case 'image':
      return (
        <figure className="my-8">
          <img 
            src={block.url || block.src} 
            alt={block.caption || block.alt || ''} 
            className="w-full rounded-lg shadow-md"
          />
          {block.caption && (
            <figcaption className="text-sm text-slate-500 mt-2 text-center">
              {block.caption}
            </figcaption>
          )}
        </figure>
      );
    
    case 'youtube':
      return (
        <div className="my-8 aspect-video rounded-lg overflow-hidden shadow-lg">
          <iframe
            src={`https://www.youtube.com/embed/${block.videoId}`}
            title="YouTube video"
            className="w-full h-full"
            allowFullScreen
            frameBorder="0"
          />
        </div>
      );
    
    case 'game_card':
      return (
        <div className="my-6 bg-gradient-to-br from-[#00204E] to-[#003366] rounded-xl p-6 text-white">
          <div className="text-xs font-bold uppercase tracking-wide text-[#ff9900] mb-2">
            {block.label || 'Featured Game'}
          </div>
          <div className="flex items-center justify-between">
            <div className="text-center">
              <div className="text-2xl font-black">{block.awayTeam || 'Away'}</div>
              {block.awayScore && <div className="text-4xl font-black mt-1">{block.awayScore}</div>}
            </div>
            <div className="text-slate-400 text-sm">VS</div>
            <div className="text-center">
              <div className="text-2xl font-black">{block.homeTeam || 'Home'}</div>
              {block.homeScore && <div className="text-4xl font-black mt-1">{block.homeScore}</div>}
            </div>
          </div>
          {block.time && (
            <div className="text-center mt-4 text-sm text-slate-300">
              {block.time}
            </div>
          )}
        </div>
      );
    
    case 'html':
      // Render raw HTML content with script execution support
      return <HtmlBlockRenderer html={block.data?.html || block.html || ''} articleId={articleId} />;
    
    case 'component':
      // Render a custom React component
      const componentName = block.data?.component;
      const Component = COMPONENT_REGISTRY[componentName];
      
      if (!Component) {
        console.warn(`Unknown component: ${componentName}`);
        return (
          <div className="my-6 p-4 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
            Component "{componentName}" not found
          </div>
        );
      }
      
      return (
        <Suspense fallback={
          <div className="my-6 p-8 bg-slate-100 rounded-lg animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/3 mb-4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        }>
          <div className="my-6">
            <Component {...(block.data?.props || {})} />
          </div>
        </Suspense>
      );
    
    default:
      return null;
  }
};

export default InteractiveArticleViewer;
